import { z } from "zod";

export const seniorityLevel = {
  1: "Intern",
  2: "Entry Level",
  3: "Junior",
  4: "Mid Level",
  5: "Senior",
  6: "Lead",
  7: "Manager",
  8: "Director",
  9: "VP",
  10: "Executive/C-suite",
};

// Base schema for all metrics with reasoning
export const baseMetricSchema = z.object({
  reasoning: z
    .string()
    .describe(
      "Brief markdown-formatted explanation with Score Summary and Resume Adjustments to improve this metric, do not mention the score number in the explanation",
    ),
});

export const hssSchema = baseMetricSchema.extend({
  job_hard_skills: z
    .array(
      z.object({
        skill: z.string().describe("skill name in lower_case_snake_case format"),
        category: z
          .string()
          .describe("skill category (e.g., programming_languages, frameworks, tools, platforms, methodologies)"),
        importance: z
          .enum(["must_have", "highly_preferred", "nice_to_have"])
          .describe("importance level based on job description context"),
        context: z
          .string()
          .optional()
          .describe("original context from job description where this skill was mentioned"),
      }),
    )
    .default([])
    .describe("structured hard skills extracted from job description with importance weighting"),
  
  resume_hard_skills: z
    .array(
      z.object({
        skill: z.string().describe("skill name in lower_case_snake_case format"),
        category: z
          .string()
          .describe("skill category (e.g., programming_languages, frameworks, tools, platforms, methodologies)"),
        evidence_type: z
          .enum(["explicit", "inferred", "related"])
          .describe("how the skill was identified - explicit: directly mentioned, inferred: demonstrated through experience, related: similar/equivalent skill"),
        confidence: z
          .enum(["high", "medium", "low"])
          .describe("confidence level of skill identification"),
        evidence: z
          .string()
          .describe("specific evidence from resume supporting this skill identification"),
        related_to: z
          .string()
          .optional()
          .describe("if evidence_type is 'related', the job requirement this skill maps to"),
      }),
    )
    .default([])
    .describe("structured hard skills identified from resume with confidence and evidence"),
  
  skill_matches: z
    .array(
      z.object({
        job_skill: z.string().describe("required skill from job description"),
        resume_skill: z.string().describe("matching skill from resume"),
        match_type: z
          .enum(["exact", "equivalent", "hierarchical", "inferred"])
          .describe("type of match - exact: same skill, equivalent: different names same skill, hierarchical: specific skill covers general requirement, inferred: skill demonstrated through experience"),
        confidence: z
          .enum(["high", "medium", "low"])
          .describe("confidence level of the match"),
        explanation: z
          .string()
          .describe("brief explanation of why this is considered a match"),
        job_importance: z
          .enum(["must_have", "highly_preferred", "nice_to_have"])
          .describe("importance level of the job requirement"),
      }),
    )
    .default([])
    .describe("detailed skill matching analysis with confidence and explanation"),
});

export type HSSSchema = z.infer<typeof hssSchema>;

export const mrcSchema = baseMetricSchema.extend({
  jd_mandatory_requirements: z
    .array(
      z.object({
        requirement: z.string().describe("mandatory requirement"),
        criticality: z
          .enum(["critical", "high", "medium", "low"])
          .describe("criticality of the requirement"),
        type: z
          .enum(["requirement", "offer", "bonus"])
          .describe("whether this is a requirement of the candidate, something offered by the company, or a bonus/nice-to-have skill"),
      }),
    )
    .describe("deduplicated, lower‑case, snake_case phrases that the JD marks as mandatory"),
  resume_matching_requirements: z
    .array(
      z.object({
        requirement: z.string().describe("requirement that is satisfied"),
        match_type: z
          .enum(["exact", "inferred", "partial"])
          .describe("type of match - exact for direct matches, inferred for related skills, partial for incomplete matches"),
        confidence: z
          .enum(["high", "medium", "low"])
          .optional()
          .describe("confidence level for inferred matches only - high: very similar skills, medium: related skills, low: loosely related"),
        related_skill: z
          .string()
          .optional()
          .describe("for inferred matches, the related skill from resume that provides the match"),
        explanation: z
          .string()
          .optional()
          .describe("brief explanation of why this is considered a match, especially for inferred/partial matches"),
      }),
    )
    .describe("requirements that are satisfied in the résumé with match details"),
  red_flags: z
    .array(
      z.object({
        flag_type: z
          .enum(["location_mismatch", "sponsorship_required", "core_experience_mismatch", "critical_requirement_missing", "other"])
          .describe("type of red flag identified"),
        title: z.string().describe("brief title of the red flag (e.g., 'Location Mismatch')"),
        description: z.string().describe("detailed explanation of the red flag issue"),
        severity: z
          .enum(["blocking", "major", "minor"])
          .describe("severity level - blocking: absolute deal-breaker, major: significant concern, minor: worth noting"),
      }),
    )
    .default([])
    .describe("critical issues that may disqualify the candidate"),
});

export type MRCSchema = z.infer<typeof mrcSchema>;

export const kcSchema = baseMetricSchema.extend({
  job_keywords: z
    .array(z.string())
    .describe("top deduplicated, lower‑case keywords / noun‑phrases explicitly found in the JD"),
  resume_keywords: z
    .array(z.string())
    .describe("subset of job_keywords that appear verbatim (case‑insensitive) in the résumé"),
});

export type KCSchema = z.infer<typeof kcSchema>;

export const ariSchema = baseMetricSchema.extend({
  job_goals: z
    .array(z.string())
    .describe("key business goals, objectives, or responsibilities from the job description"),
  job_requirements: z
    .array(
      z.object({
        requirement: z.string().describe("specific requirement from job description"),
        category: z
          .enum(["technical_skill", "experience", "achievement", "responsibility", "qualification"])
          .describe("category of the requirement"),
        importance: z
          .enum(["must_have", "highly_preferred", "nice_to_have"])
          .describe("importance level based on job description context"),
      }),
    )
    .describe("structured requirements extracted from job description for gap analysis"),
  resume_achievements: z
    .array(
      z.object({
        achievement: z
          .string()
          .describe("impact-oriented achievement or accomplishment from the resume, including action verbs and outcomes"),
        metrics: z
          .array(z.string())
          .describe("specific numbers, percentages, or metrics mentioned"),
        context: z
          .string()
          .describe("work context where this achievement occurred (company, role, timeframe)"),
        action_verbs: z
          .array(z.string())
          .describe("strong action verbs identified in this achievement (delivered, led, improved, scaled, etc.)"),
        impact_type: z
          .enum(["revenue", "cost_savings", "efficiency", "scale", "innovation", "leadership", "quality", "other"])
          .describe("type of business impact demonstrated"),
        recency_years: z
          .number()
          .optional()
          .describe("approximate years since this achievement (0-1 = very recent, 1-3 = recent, 3-5 = moderate, 5+ = older)"),
        weight: z
          .object({
            recency: z.enum(["high", "medium", "low"]).describe("weight based on how recent the achievement is"),
            impact: z.enum(["high", "medium", "low"]).describe("weight based on quantifiable business impact"),
            relevance: z.enum(["high", "medium", "low"]).describe("weight based on relevance to commercial/production setting"),
            overall: z.enum(["high", "medium", "low"]).describe("combined overall weight of this achievement"),
          })
          .describe("weighting factors for this achievement"),
        inferred_skills: z
          .array(z.string())
          .describe("skills that can be reasonably inferred from this achievement context (e.g., MLOps from 'scaled production models')"),
      }),
    )
    .describe("impact-oriented achievements extracted from resume with comprehensive analysis and weighting"),
  gap_analysis: z
    .array(
      z.object({
        requirement: z.string().describe("specific requirement from job description"),
        status: z
          .enum(["found", "partially_found", "not_found", "inferred"])
          .describe("whether requirement was found in resume"),
        evidence: z
          .string()
          .optional()
          .describe("specific evidence from resume if found or partially found"),
        confidence: z
          .enum(["high", "medium", "low"])
          .optional()
          .describe("confidence level for inferred or partially found items"),
        suggestion: z
          .string()
          .describe("specific suggestion for improvement if not found or partially found"),
        inferred_from: z
          .string()
          .optional()
          .describe("context or experience that allows inference of this skill/requirement"),
      }),
    )
    .describe("explicit gap analysis showing what job requirements were found vs missing in resume"),
  implicit_skills: z
    .array(
      z.object({
        skill: z.string().describe("skill that can be inferred from context"),
        evidence_context: z
          .string()
          .describe("specific context from resume that implies this skill (e.g., 'Amazon Science production models' implies MLOps)"),
        confidence: z
          .enum(["high", "medium", "low"])
          .describe("confidence level of the inference"),
        related_requirements: z
          .array(z.string())
          .describe("job requirements this inferred skill addresses"),
      }),
    )
    .describe("skills inferred from work context and experience rather than explicit mentions"),
});

export type ARISchema = z.infer<typeof ariSchema>;

export const kiciSchema = baseMetricSchema.extend({
  jd_keywords: z
    .array(z.string())
    .describe("key technical/domain terms from JD (lowercase, no duplicates)"),

  resume_bullets: z
    .array(
      z.object({
        text: z.string().describe("bullet text"),
        keywords: z.array(z.string()).describe("jd_keywords found in this bullet"),
        has_action: z.boolean().describe("starts with action verb (built, led, etc.)"),
        has_outcome: z.boolean().describe("contains numbers/metrics (%,$,+,etc.)"),
      }),
    )
    .describe("bullets containing jd_keywords"),
});

export type KICISchema = z.infer<typeof kiciSchema>;

export const dieSchema = baseMetricSchema.extend({
  jd_domains: z
    .array(z.string())
    .max(10)
    .describe("up to 10 primary industry domains from JD (e.g., fintech, healthcare, e_commerce)"),

  resume_domains: z
    .array(z.string())
    .max(15)
    .describe("up to 15 industry domains from resume (company industries, experience areas)"),

  resume_positions: z
    .array(
      z.object({
        domain: z.string().describe("primary industry domain"),
        years: z.number().describe("total years of experience in this domain"),
      }),
    )
    .max(10)
    .describe("up to 10 positions with domain and total years experience"),

  skill_transferability: z
    .array(
      z.object({
        source_domain: z.string().describe("candidate's domain of experience"),
        target_domain: z.string().describe("target domain from job description"),
        transferable_skill: z.string().describe("specific skill or capability that transfers"),
        transferability_strength: z
          .enum(["high", "medium", "low"])
          .describe("strength of transferability - high: directly applicable, medium: requires adaptation, low: foundational relevance"),
        source_evidence: z.string().describe("specific evidence from resume demonstrating this skill"),
        target_application: z.string().describe("how this skill applies to the target domain/role"),
        explanation: z.string().describe("detailed explanation of the transferability connection"),
      }),
    )
    .max(15)
    .describe("up to 15 cross-domain skill transferability analyses showing how candidate's experience applies to target domain"),
  
  resume_seniority: z.number().describe("seniority level from Intern (1) to Executive (10)"),
  jd_seniority: z.number().describe("seniority level from Intern (1) to Executive (10)"),
});

export type DIESchema = z.infer<typeof dieSchema>;



export const ssaSchema = baseMetricSchema.extend({
  job_soft_skills: z
    .array(z.string())
    .describe("deduplicated, lower‑case, snake_case phrases from the JD"),
  resume_soft_skills: z
    .array(z.string())
    .describe("deduplicated, lower‑case, snake_case phrases from the résumé"),
});

export type SSASchema = z.infer<typeof ssaSchema>;

// Schema for LLM response - only facts, no counts or statistics
export const lcqLLMSchema = baseMetricSchema.extend({
  problematic_sentences: z
    .array(
      z.object({
        sentence: z.string().describe("sentence from the résumé that exhibits an issue"),
        severity: z
          .enum(["critical", "major", "minor", "stylistic"])
          .describe("severity level: critical=incomprehensible/major errors, major=grammar/clarity issues affecting meaning, minor=small grammar/spelling errors, stylistic=resume convention suggestions"),
        issue_type: z
          .enum(["grammar", "spelling", "punctuation", "clarity", "coherence", "word_choice", "style"])
          .describe("type of issue identified"),
        explanation: z
          .string()
          .describe("brief explanation of the issue and why it was flagged at this severity level"),
        suggestion: z
          .string()
          .optional()
          .describe("suggested improvement or fix for the issue"),
      }),
    )
    .describe("sentences from the résumé that exhibit language, grammar, or clarity issues with severity classification"),
  bullet_points: z
    .array(
      z.object({
        text: z.string().describe("bullet point text exactly as it appears"),
        has_action_verb: z
          .boolean()
          .describe(
            "true if first meaningful word (ignoring bullets like '-', '•', '*') is a strong action verb like achieved, built, delivered, engineered, led, optimized",
          ),
      }),
    )
    .max(20)
    .describe("up to 20 bullet points with action verb classification"),
});

// Full schema including computed statistics (for final result)
export const lcqSchema = lcqLLMSchema.extend({
  summary: z
    .object({
      total_issues: z.number().describe("total number of issues found"),
      total_sentences: z.number().describe("total number of sentences analyzed"),
      error_rate: z.number().describe("percentage of sentences with issues"),
      weighted_error_rate: z.number().describe("severity-weighted error rate percentage"),
      severity_breakdown: z
        .object({
          critical: z.number(),
          major: z.number(),
          minor: z.number(),
          stylistic: z.number(),
        })
        .describe("count of issues by severity level"),
      most_common_severity: z.string().nullable().describe("most frequent severity level"),
    })
    .describe("computed summary statistics of language and clarity analysis"),
  action_verb_summary: z
    .object({
      total_bullets: z.number().describe("total number of bullet points analyzed"),
      action_verb_bullets: z.number().describe("number of bullet points with action verbs"),
      action_verb_percentage: z.number().describe("percentage of bullet points with action verbs"),
    })
    .describe("computed summary statistics of action verb usage"),
});

export type LCQLLMSchema = z.infer<typeof lcqLLMSchema>;
export type LCQSchema = z.infer<typeof lcqSchema>;



export const rwSchema = baseMetricSchema.extend({
  job_skills: z
    .array(z.string())
    .describe("deduplicated, lower‑case, snake_case hard‑skills explicitly required in the JD"),
  resume_skill_evidence: z.array(
    z.object({
      skill: z.string().describe("hard‑skill from job requirements"),
      year: z.number().int().describe("most recent four‑digit year of evidence"),
      evidence_type: z
        .enum(["explicit", "inferred", "transferable"])
        .describe("explicit: directly mentioned, inferred: demonstrated through related experience, transferable: applicable from different context"),
      strength: z
        .enum(["high", "medium", "low"])
        .describe("strength of evidence - high: clear demonstration, medium: reasonable inference, low: weak connection"),
      evidence_context: z
        .string()
        .describe("brief explanation of how this skill is evidenced in the resume"),
      related_skills: z
        .array(z.string())
        .optional()
        .describe("related or equivalent skills that support this inference"),
    }),
  ),
  skill_gaps: z
    .array(
      z.object({
        skill: z.string().describe("required skill not found in resume"),
        potential_equivalents: z
          .array(z.string())
          .describe("similar skills found in resume that could be relevant"),
        transferability: z
          .enum(["high", "medium", "low", "none"])
          .describe("how transferable candidate's existing skills are to this requirement"),
      }),
    )
    .describe("skills required by job but not evidenced in resume"),
});

export enum MetricKeyEnum {
  ARI = "ari",
  LCQ = "lcq",
  RW = "rw",
  SSA = "ssa",
  DIE = "die",
  KICI = "kici",
  KC = "kc",
  MRC = "mrc",
  HSS = "hss",
}

export type RWSchema = z.infer<typeof rwSchema>;

export interface MetricCalculateResult<T> {
  metric: string;
  key: string;
  weight: number;
  description: string;
  score: number;
  object: T;
  weightedScore: number;
}

interface AnalyzeResumeMetricResult<T> extends Omit<MetricCalculateResult<T>, "score" | "key"> {}

export interface AnalyzeResumeResult {
  overallWeightedScore: number;
  metrics: {
    [MetricKeyEnum.ARI]: AnalyzeResumeMetricResult<ARISchema>;
    [MetricKeyEnum.LCQ]: AnalyzeResumeMetricResult<LCQSchema>;
    [MetricKeyEnum.SSA]: AnalyzeResumeMetricResult<SSASchema>;
    [MetricKeyEnum.DIE]: AnalyzeResumeMetricResult<DIESchema>;
    [MetricKeyEnum.KICI]: AnalyzeResumeMetricResult<KICISchema>;

    [MetricKeyEnum.KC]: AnalyzeResumeMetricResult<KCSchema>;
    [MetricKeyEnum.MRC]: AnalyzeResumeMetricResult<MRCSchema>;
    [MetricKeyEnum.HSS]: AnalyzeResumeMetricResult<HSSSchema>;
    [MetricKeyEnum.RW]: AnalyzeResumeMetricResult<RWSchema>;
  };
}
